import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, Typography, Slider, Flex, Alert } from 'antd'
import type { TestEditorContext } from '../../index'

const { Title, Text } = Typography

const SLIDER_WIDTH = 380
const SLIDER_MARKS = { 5: '5%', 25: '25%', 50: '50%', 75: '75%', 100: '100%' }

export const TrafficSplitPage: React.FC = () => {
  const { baseFlow, leadFlows, setLeadFlows } =
    useOutletContext<TestEditorContext>()

  if (leadFlows.length === 0 && !baseFlow) {
    return (
      <Alert
        title="No Variants Created"
        description="Please go back and select at least two lead flows to configure traffic split."
        type="info"
        showIcon
      />
    )
  }

  const totalTraffic = leadFlows.reduce(
    (sum, f) => sum + f.traffic_percentage,
    0,
  )
  const remaining = 100 - totalTraffic

  const getMaxForFlow = (flowId: string) => {
    const othersTotal = leadFlows
      .filter((f) => f.flow_id !== flowId)
      .reduce((sum, f) => sum + f.traffic_percentage, 0)
    return 100 - othersTotal
  }

  const handleTrafficChange = (flowId: string, value: number) => {
    setLeadFlows(
      leadFlows.map((f) =>
        f.flow_id === flowId ? { ...f, traffic_percentage: value } : f,
      ),
    )
  }

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Configure Traffic Split</Title>
          <Text type="secondary">
            Allocate traffic percentages for each variant. Remaining traffic
            goes to the default flow.
          </Text>
        </div>

        <Card
          title="Traffic Allocation"
          style={{ maxWidth: 720 }}
          extra={
            <Text
              strong
              style={{ color: remaining >= 0 ? '#52c41a' : '#f5222d' }}
            >
              Remaining: {remaining}%
            </Text>
          }
        >
          <Flex vertical gap="large" style={{ width: '100%' }}>
            {baseFlow && (
              <Flex align="center" gap="large">
                <Text
                  strong
                  ellipsis={{ tooltip: `${baseFlow.name} (Control)` }}
                  style={{ width: 180, flexShrink: 0 }}
                >
                  {baseFlow.name} (Control)
                </Text>
                <Text type="secondary">
                  Remaining: {remaining < 0 ? 0 : remaining}%
                </Text>
              </Flex>
            )}
            {leadFlows.map((flow) => {
              const max = getMaxForFlow(flow.flow_id)
              return (
                <Flex key={flow.flow_id} align="center" gap="large">
                  <Text
                    strong
                    ellipsis={{ tooltip: flow.flow_name }}
                    style={{ width: 180, flexShrink: 0 }}
                  >
                    {flow.flow_name}
                  </Text>
                  <Slider
                    min={5}
                    max={100}
                    step={5}
                    marks={SLIDER_MARKS}
                    value={flow.traffic_percentage}
                    onChange={(value) =>
                      handleTrafficChange(flow.flow_id, Math.min(value, max))
                    }
                    tooltip={{ formatter: (value) => `${value}%` }}
                    style={{ width: SLIDER_WIDTH, marginBottom: 0 }}
                  />
                  <Text strong style={{ width: 44, textAlign: 'right' }}>
                    {flow.traffic_percentage}%
                  </Text>
                </Flex>
              )
            })}
          </Flex>
        </Card>
      </Flex>
    </div>
  )
}
